import type { ScoringRule } from "@/types/scoring-rules";

export interface DraftRule {
  key: string;
  /**
   * Stabile Identität der UI-Aggregat-Zeile, zu der dieser Draft gehört.
   * Wird einmal bei der Entstehung vergeben und danach von splitAggregates
   * und aggregateDrafts unverändert durchgereicht — NIE aus weight, tags
   * oder reasonText abgeleitet, sonst wandert die Zeilen-Identität beim
   * Tippen und React unmountet die Zeile (F-122).
   *
   * Reiner Frontend-State: aggregateId wird nicht persistiert (saveAll in
   * ScoringEditor.tsx baut den Payload ohne dieses Feld).
   */
  aggregateId: string;
  targetValue: string;
  weight: number;
  reasonText: string;
  active: boolean;
}

export type DirtyMap = Record<string, DraftRule[]>;

/**
 * Aggregat-Identität für aus der DB geladene Regeln.
 *
 * Bestehende scoring_rules kennen kein aggregateId, und getDraftsForAnswer
 * baut die Drafts bei jedem Render neu — die Identität muss deshalb
 * deterministisch aus der Regel ableitbar sein, nicht zufällig vergeben.
 * (weight, reasonText) ist dabei genau das Kriterium, nach dem geladene
 * Regeln bisher zu einer Synonym-Zeile gruppiert wurden.
 *
 * Sobald die Antwort dirty ist, kommen die Drafts aus dem State und die
 * einmal abgeleitete Identität bleibt eingefroren.
 */
export function deriveAggregateId(weight: number, reasonText: string): string {
  return `agg:${weight}__${reasonText}`;
}

export function createDraftFromRule(rule: ScoringRule): DraftRule {
  const reasonText = rule.reasonText ?? "";
  return {
    key: rule.id,
    aggregateId: deriveAggregateId(rule.weight, reasonText),
    targetValue: rule.targetValue,
    weight: rule.weight,
    reasonText,
    active: rule.active,
  };
}

export function createEmptyDraft(): DraftRule {
  return {
    key: crypto.randomUUID(),
    aggregateId: crypto.randomUUID(),
    targetValue: "",
    weight: 1,
    reasonText: "",
    active: true,
  };
}

/**
 * UI-Aggregat für das Synonym-Pattern: mehrere Tag-Chips teilen sich eine
 * (weight, reasonText)-Kombination innerhalb einer Antwort.
 *
 * Beim Save wird jedes DraftRuleAggregate in N DraftRule-Einträge gesplittet,
 * einer pro Tag. Die Aggregation ist reine UI-Schicht.
 */
export interface DraftRuleAggregate {
  aggregateId: string;
  tags: string[];
  weight: number;
  reasonText: string;
  active: boolean;
}

export function aggregateDrafts(drafts: DraftRule[]): DraftRuleAggregate[] {
  const groupMap = new Map<string, DraftRuleAggregate>();
  for (const draft of drafts) {
    const existing = groupMap.get(draft.aggregateId);
    if (existing) {
      if (draft.targetValue && !existing.tags.includes(draft.targetValue)) {
        existing.tags.push(draft.targetValue);
      }
    } else {
      groupMap.set(draft.aggregateId, {
        aggregateId: draft.aggregateId,
        tags: draft.targetValue ? [draft.targetValue] : [],
        weight: draft.weight,
        reasonText: draft.reasonText,
        active: draft.active,
      });
    }
  }
  return Array.from(groupMap.values());
}

export function splitAggregates(
  aggregates: DraftRuleAggregate[],
): DraftRule[] {
  const drafts: DraftRule[] = [];
  for (const agg of aggregates) {
    if (agg.tags.length === 0) {
      // Zeile ohne Tags — der Zustand direkt nach "+ Regel hinzufügen".
      // Ohne Platzhalter-Draft ginge die Zeile beim nächsten Round-Trip
      // verloren (stiller Datenverlust, F-122). Der leere targetValue wird
      // beim Speichern herausgefiltert, der Payload bleibt unverändert.
      drafts.push({
        key: `${agg.aggregateId}__`,
        aggregateId: agg.aggregateId,
        targetValue: "",
        weight: agg.weight,
        reasonText: agg.reasonText,
        active: agg.active,
      });
      continue;
    }
    for (const tag of agg.tags) {
      drafts.push({
        key: `${agg.aggregateId}__${tag}`,
        aggregateId: agg.aggregateId,
        targetValue: tag,
        weight: agg.weight,
        reasonText: agg.reasonText,
        active: agg.active,
      });
    }
  }
  return drafts;
}
